/**
 * VFXEffectRegistry - Data-driven registry for all visual effect definitions
 *
 * Every visual effect is a named config object (primitives + tweens),
 * not inline rendering code. Effects can be registered, retrieved,
 * and live-patched at runtime via the Ability Editor UI.
 */

import { gameLogger } from '../../utils/GameLogger';
import type { AnimatedTextOptions } from './text/types';

// =============================================================================
// TYPES
// =============================================================================

export interface VFXTweenConfig {
  x?: number;
  y?: number;
  alpha?: number;
  scale?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  duration: number;
  ease?: string;
  yoyo?: boolean;
  repeat?: number;
  delay?: number;
}

export interface VFXScatterConfig {
  /** Number of copies to spawn in a ring */
  count: number;
  /** Ring radius in pixels */
  radius: number;
}

export interface VFXOrbitConfig {
  /** Distance from center */
  radius: number;
  /** Rotations per second */
  speed: number;
  /** Starting angle in radians */
  startAngle?: number;
  /** Whether to rotate the primitive itself to face the direction of travel */
  orientToPath?: boolean;
  /** Local offset from the world/follow anchor to the orbit's center. */
  centerOffsetX?: number;
  centerOffsetY?: number;
}

export interface VFXPrimitive {
  type: 'circle' | 'ellipse' | 'star' | 'rect' | 'polygon' | 'particles' | 'entity_override'
    | 'arc' | 'beam' | 'shockwave' | 'line' | 'cone' | 'ribbon';
  /** If type is 'particles', this is the emitter key in EnhancedParticleManager */
  particleEffectId?: string;
  /** Number of particles to burst */
  particleCount?: number;
  /** If type is 'entity_override', the visual properties to apply to the target sprite */
  entityAlpha?: number;
  entityTint?: number;
  entityScale?: number;
  // Spawn params (relative to effect origin)
  offsetX?: number;
  offsetY?: number;
  // Shape params
  radius?: number;
  width?: number;
  height?: number;
  // Star-specific
  points?: number;
  innerRadius?: number;
  outerRadius?: number;
  // Polygon-specific
  polygonPoints?: number[];
  // Arc-specific (sweeping arc path for melee swings). Angles in DEGREES,
  // right-facing basis (0° = ahead, negative = up); ShapeSpawner converts to
  // radians and mirrors across the vertical axis when the spawn carries
  // `flipX: true` (left-facing casts).
  arcStartAngle?: number;
  arcEndAngle?: number;
  arcRadius?: number;
  // Cone-specific (a FILLED angular sector / wedge — breath attacks, frost cones,
  // directional novas; the filled fan the stroked `arc` can't make). Angles in
  // DEGREES, same right-facing basis + flipX mirroring as `arc`. Defaults to
  // `radius` for the reach when `coneRadius` is omitted.
  coneStartAngle?: number;
  coneEndAngle?: number;
  coneRadius?: number;
  // Beam-specific (straight or jagged line for lightning/channels)
  beamLength?: number;
  beamWidth?: number;
  /** Number of jagged segments for lightning-style beams */
  beamSegments?: number;
  /**
   * Two-endpoint link (beam-builders spec §4.1): BeamLinkSystem stretches +
   * rotates this primitive from the effect's caster anchor to a second
   * entity every frame (spawn override `linkTargetId`). Works on `beam`
   * primitives (drawn from (0,0) along +x — rotation-safe) and, with
   * `mote`, on small shapes that FLOW along the link (drain packets).
   * Linked primitives ignore flipX (rotation supersedes facing) and must
   * not author x/y tweens (the link owns position). Targetless casts
   * degrade to a facing-aligned fixed-length link (the HitscanBehavior
   * floor).
   */
  link?: {
    to: 'target';
    /** Clamp for the stretch (scaleX ≤ maxLengthPx/beamLength). */
    maxLengthPx?: number;
    /** Flow along the link instead of stretching across it: position cycles
     *  endpoint→endpoint every periodMs (drain motes). `phase` staggers
     *  copies (0..1); direction defaults to 'toCaster' (siphon read). */
    mote?: { periodMs: number; phase?: number; direction?: 'toCaster' | 'toTarget' };
  };
  // Ribbon-specific (trails-and-afterimages spec §4.1) — a fading polyline
  // redrawn each frame from a TrailSystem position-history ring buffer.
  // ONE graphics object per ribbon; never routed through ShapeSpawner and
  // never tweened (the history owns all motion). Ribbon primitives only
  // live in `category: 'trail'` defs and require a `trailKey` spawn
  // override (the fed entity/projectile id) to attach.
  /** How much history (ms) the ribbon shows. Default 240. */
  ribbonLengthMs?: number;
  /** Ring-buffer sample cap (≈ lengthMs / frame). Default 24. */
  ribbonMaxPoints?: number;
  /** Width steps down toward the tail. Default true. */
  ribbonTaper?: boolean;
  /** Alpha steps down toward the tail. Default true. */
  ribbonFade?: boolean;
  /** Multi-tone under-glow stroke color (distinct from `color`). */
  ribbonGlowColor?: number;
  // Shockwave-specific (expanding concentric ring)
  shockwaveInnerRadius?: number;
  shockwaveOuterRadius?: number;
  // Line-specific (multi-point line for lightning chains, cracks)
  linePoints?: Array<{ x: number; y: number }>;
  lineWidth?: number;
  /** Pixel variance for jagged line effects */
  lineJitter?: number;
  // Appearance
  color?: number;
  alpha?: number;
  depth?: number;
  stroke?: { color: number; width: number };
  /**
   * Render into the GLOW/bloom layer (VFX-evolution M1.S3): additive-blended
   * + the layer's single blur pass = luminous halo. Use for fairy glow,
   * impact flares, caster auras, light blobs. Cookbook: VFX_BUILDER_COOKBOOK.
   */
  glow?: boolean;
  // Animation
  tween?: VFXTweenConfig;
  // Scatter: spawn N copies in a ring pattern
  scatter?: VFXScatterConfig;
  // Orbit: make the primitive orbit the center
  orbit?: VFXOrbitConfig;
}

/** `'zone'` (zone-builders spec §6): world-anchored persistent ground zones.
 *  Distinct from `'aura'` (entity-following) so zone defs are enumerable
 *  (getByCategory) and guardable — every zone def MUST carry a top-level
 *  `duration` ceiling and must never set `followTarget` (PF-ZONE-* preflight
 *  guards; the ice_wall leak class). */
export type VFXCategory = 'burst' | 'trail' | 'aura' | 'projectile_vfx' | 'screen' | 'status' | 'orbit' | 'particle_preset' | 'zone';

export interface VFXFloatingTextConfig {
  text: string;
  color?: string;
  fontSize?: string;
  duration?: number;
  offsetY?: number;
  style?: 'damage' | 'heal' | 'critical' | 'info' | 'ability' | 'xp';
  bounce?: boolean;
  fade?: boolean;
}

export interface VFXEffectConfig {
  id: string;
  name: string;
  category: VFXCategory;
  /** Description for the UI */
  description?: string;
  /** Total duration before force destroying primitives (useful for looping effects) */
  duration?: number;
  /** Whether the effect should follow a target entity ID */
  followTarget?: string;
  /** Primitives to spawn */
  primitives: VFXPrimitive[];
  /** Pure-shape status playback retries below this admitted count (default 1).
   * Partial allocation is rolled back, never exempt from the shared budget.
   * Mixed and non-shape definitions keep one-shot side effects and do not retry. */
  minimumShapes?: number;
  /** Optional screen effects */
  screenShake?: { intensity: number; duration: number };
  screenFlash?: { color: number; duration: number };
  screenTint?: { color: number; duration: number };
  /** Optional floating text */
  floatingText?: VFXFloatingTextConfig;
  /** Optional animated text (typewriter, pop, shake, etc.) */
  animatedText?: AnimatedTextOptions;
}

// =============================================================================
// REGISTRY
// =============================================================================

class VFXEffectRegistryImpl {
  private effects = new Map<string, VFXEffectConfig>();

  /**
   * Register a VFX effect definition
   */
  public register(config: VFXEffectConfig): void {
    if (this.effects.has(config.id)) {
      gameLogger.debug('vfx', `[VFXEffectRegistry] Overwriting effect: ${config.id}`);
    }
    this.effects.set(config.id, config);
  }

  /**
   * Register multiple effects at once
   */
  public registerAll(configs: VFXEffectConfig[]): void {
    for (const config of configs) {
      this.register(config);
    }
  }

  /**
   * Get an effect config by ID
   */
  public get(id: string): VFXEffectConfig | undefined {
    return this.effects.get(id);
  }

  /**
   * Check if an effect exists
   */
  public has(id: string): boolean {
    return this.effects.has(id);
  }

  /**
   * Get all registered effect IDs
   */
  public getAllIds(): string[] {
    return Array.from(this.effects.keys());
  }

  /**
   * Get all effects
   */
  public getAll(): VFXEffectConfig[] {
    return Array.from(this.effects.values());
  }

  /**
   * Get effects by category
   */
  public getByCategory(category: VFXCategory): VFXEffectConfig[] {
    return Array.from(this.effects.values()).filter(e => e.category === category);
  }

  /**
   * Live-patch an effect config at runtime (for UI sliders)
   */
  public updateEffect(id: string, patch: Partial<VFXEffectConfig>): void {
    const existing = this.effects.get(id);
    if (!existing) {
      gameLogger.warn('vfx', `[VFXEffectRegistry] Cannot update unknown effect: ${id}`);
      return;
    }
    this.effects.set(id, { ...existing, ...patch });
    gameLogger.info('vfx', `[VFXEffectRegistry] Updated effect: ${id}`);
  }

  /**
   * Update a specific primitive within an effect
   */
  public updatePrimitive(effectId: string, primitiveIndex: number, patch: Partial<VFXPrimitive>): void {
    const effect = this.effects.get(effectId);
    if (!effect || !effect.primitives[primitiveIndex]) return;
    effect.primitives[primitiveIndex] = { ...effect.primitives[primitiveIndex], ...patch };
  }
}

// Singleton
export const VFXEffectRegistry = new VFXEffectRegistryImpl();
